import { AnalyticIdentifier, GitHubAppInstallQueryField, GitHubAppInstallStatus } from '@common/neo.tax';
import {
  Alert,
  colors,
  CompanyLogoIcon,
  CompanyLogoStatus,
  Input,
  ModalHeader,
  ProgressBar,
} from '@ui-components/neo.tax';
import { useEffect, useState } from 'react';

import { TICKETING_PROVIDER_NAMES } from '@neo.tax/shared/constants';
import { GitHubInstallationStatus, TicketingProvider } from '@neo.tax/shared/graphql';
import {
  useGitHubAppAuthorization,
  useGitHubAppConnect,
  useGitHubAppInstallStatus,
  useUrlQueryParam,
} from '@neo.tax/shared/hooks';
import { useAnalyticId } from '@neo.tax/shared/state';

import { ProviderLogo } from '../../../provider-logo';
import { StyledConnectGitHub, StyledGitHubBody, StyledGitHubPrompt } from './connect-github.styled';
import { StyledAutomationsIcon, StyledSingleActionButton } from './connect-provider.styled';
import { MultipleActions } from './MultipleActions';

const POLL_INTERVAL_MS = 2000;
// Stop polling after this long if the installation.created webhook never lands — install abandoned,
// org-slug mismatch, ambiguous correlation, or the App's webhook URL pointing at a host that no
// longer exists (a routine hazard for the shared local/preview Apps; see the GitHub App runbook).
// A minute is well past the webhook's normal latency, and none of those causes resolve by waiting
// longer, so falling through to the reconnect CTA early beats a silent wait — re-installing is cheap
// and the PENDING row is re-armed idempotently.
const MAX_POLL_MS = 60 * 1000;

const ORGANIZATION_INPUT_ID = 'github-app-organization';

interface ConnectGitHubAppProps {
  providerName: TicketingProvider;
  close: () => void;
  restart: () => void;
  /**
   * The organization this connection is already for, when resuming or reconnecting one from the
   * Data Sources list. Not a convenience: the install upsert writes whatever is typed here over the
   * row's organization, so a typo on "resume" silently repoints the existing connection at a
   * different org and the install can then never be correlated.
   */
  initialOrganizationSlug?: string | null;
}

/**
 * GitHub App connect sub-flow (PLAT-386). Unlike the Merge providers (modal + callback), this is a
 * full-page redirect to GitHub's install screen and back. On return (`?connected_github_app=pending`)
 * the slide-over re-opens here and polls the connection until the installation.created webhook
 * (PLAT-385) flips it ACTIVE.
 */
export const ConnectGitHubApp = ({
  close,
  initialOrganizationSlug,
  providerName,
  restart,
}: ConnectGitHubAppProps): React.ReactElement => {
  const [organizationSlug, setOrganizationSlug] = useState(initialOrganizationSlug ?? '');
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const { error, install, loading } = useGitHubAppConnect();
  const { authorize, error: authorizationError, loading: authorizationLoading } = useGitHubAppAuthorization();
  const {
    installationStatus,
    organization: connectionOrganization,
    refreshInstallationStatus,
    startPolling,
    stopPolling,
  } = useGitHubAppInstallStatus();
  const [refreshing, setRefreshing] = useState(false);

  const cancelConnectionId = useAnalyticId(AnalyticIdentifier.LINK_TICKETING_CANCEL);
  const connectId = useAnalyticId(AnalyticIdentifier.LINK_TICKETING);
  const checkConnectionId = useAnalyticId(AnalyticIdentifier.CHECK_GITHUB_APP_CONNECTION);
  const providerFullName = TICKETING_PROVIDER_NAMES[providerName];

  // Read once: closing strips the param from the URL, and the slide must not switch views while it
  // animates out. A new install return is a full page load, which remounts this anyway.
  const connectedParamOnMount = useUrlQueryParam(GitHubAppInstallQueryField.CONNECTED_STATUS);
  const [connectedParam] = useState(connectedParamOnMount);
  const isReturningFromInstall = connectedParam === GitHubAppInstallStatus.PENDING;
  const installFailed = connectedParam === GitHubAppInstallStatus.FAILED;
  // The installer could only *request* the install; an org owner has to approve it. Not a polling
  // state: approval can take days, and it activates the connection through the webhook with no
  // browser attached, so the user is told to come back and check instead.
  const awaitingOwnerApproval = connectedParam === GitHubAppInstallStatus.AWAITING_APPROVAL;

  // ACTIVE is the only status that ends the wait. Re-connecting a SUSPENDED install legitimately
  // returns here while the row is still SUSPENDED — the server leaves it that way on purpose
  // (upsertPendingGitHubAppTicketingToken only re-arms DELETED, since a suspended installation
  // recovers via the `unsuspend` webhook). Gating on PENDING alone would skip polling for exactly
  // those users and drop them back on the reconnect prompt while the webhook was flipping them live.
  const isAwaitingActivation =
    isReturningFromInstall && !pollTimedOut && installationStatus !== GitHubInstallationStatus.Active;

  useEffect(() => {
    // Poll only while we're waiting for the webhook to confirm the install; stop once ACTIVE.
    // Bound the wait so an install that never completes falls through to the reconnect view.
    if (!isAwaitingActivation) {
      stopPolling();
      return undefined;
    }
    startPolling(POLL_INTERVAL_MS);
    const timeoutId = setTimeout(() => setPollTimedOut(true), MAX_POLL_MS);
    return (): void => {
      stopPolling();
      clearTimeout(timeoutId);
    };
  }, [isAwaitingActivation, startPolling, stopPolling]);

  const logoStatus: CompanyLogoStatus =
    installationStatus === GitHubInstallationStatus.Active
      ? 'success'
      : installFailed || pollTimedOut
        ? 'error'
        : 'default';

  const providerLogo = (
    <CompanyLogoIcon
      logo={<ProviderLogo provider={providerName} />}
      companyName={providerFullName}
      status={logoStatus}
    />
  );

  if (installationStatus === GitHubInstallationStatus.Active) {
    return (
      <StyledConnectGitHub>
        <ModalHeader
          icon={<StyledAutomationsIcon />}
          iconBgColor={colors.blue[50]}
          subtitle="Success"
          title="GitHub App Installed"
        />
        <StyledGitHubBody direction="column">
          {providerLogo}
          <StyledGitHubPrompt as="p" fontSize="sm">
            GitHub is connected.
          </StyledGitHubPrompt>
        </StyledGitHubBody>
        {/* Single action, so not MultipleActions: ModalActions is a two-column grid and one child
            would sit in the left column at half width. Mirrors ConnectProviderActions' success state. */}
        <StyledSingleActionButton variant="primary" rounded="regular" size="base" onClick={close} id={connectId}>
          Done
        </StyledSingleActionButton>
      </StyledConnectGitHub>
    );
  }

  if (isAwaitingActivation) {
    return (
      <StyledConnectGitHub>
        <ModalHeader
          icon={<StyledAutomationsIcon />}
          iconBgColor={colors.blue[50]}
          subtitle="1 in progress"
          title="Finishing Up"
        />
        <StyledGitHubBody direction="column">
          <StyledGitHubPrompt as="p" fontSize="sm">
            Finishing up… confirming the installation with GitHub.
          </StyledGitHubPrompt>
          <ProgressBar />
        </StyledGitHubBody>
      </StyledConnectGitHub>
    );
  }

  // The webhook may have activated the connection since this view last read it, and recovery only
  // works on a PENDING connection, so read the status fresh before sending the user to GitHub. An
  // ACTIVE result needs no further action: the success view renders from the refreshed status.
  const checkConnection = async (): Promise<void> => {
    setRefreshing(true);
    let status: GitHubInstallationStatus | null;
    try {
      status = await refreshInstallationStatus();
    } catch {
      status = null;
    } finally {
      setRefreshing(false);
    }
    if (status !== GitHubInstallationStatus.Active) {
      await authorize();
    }
  };

  const canRecoverPendingConnection = awaitingOwnerApproval || installationStatus === GitHubInstallationStatus.Pending;

  if (canRecoverPendingConnection) {
    // This view has no organization input, and returning from GitHub remounts the slide without an
    // initial slug, so the pending connection's own organization is what makes a retry possible.
    const retryOrganizationSlug = organizationSlug.trim() || connectionOrganization?.trim() || '';

    return (
      <StyledConnectGitHub>
        <ModalHeader
          icon={<StyledAutomationsIcon />}
          iconBgColor={colors.blue[50]}
          subtitle="Action needed"
          title="Finish Connecting GitHub"
        />
        <StyledGitHubBody direction="column">
          {providerLogo}
          <StyledGitHubPrompt as="p" fontSize="sm">
            {awaitingOwnerApproval
              ? 'A GitHub organization owner must approve the installation request. After they approve it, check the connection here.'
              : 'If the Neo.Tax GitHub App is installed on your organization, check the connection to finish setup.'}
          </StyledGitHubPrompt>
          {authorizationError && (
            <Alert
              severity="error"
              title="Couldn’t verify the GitHub connection"
              subtitle={<p>Confirm the App is installed and that your GitHub account can access it, then try again.</p>}
            />
          )}
        </StyledGitHubBody>
        <MultipleActions
          actions={[
            {
              button: {
                variant: 'white',
                rounded: 'regular',
                size: 'base',
                disabled: loading || retryOrganizationSlug === '',
                onClick: () => install(retryOrganizationSlug),
                children: 'Install Again',
                id: connectId,
              },
              actionText: 'Use if the App is not installed',
            },
            {
              button: {
                variant: 'primary',
                rounded: 'regular',
                size: 'base',
                disabled: authorizationLoading || refreshing,
                onClick: checkConnection,
                children: 'Check My Connection',
                id: checkConnectionId,
              },
              actionText: 'Use after an owner approves',
            },
          ]}
        />
        {/* Approval can take days, so leaving without checking has to be an explicit choice here. */}
        <StyledSingleActionButton variant="white" rounded="regular" size="base" onClick={close} id={cancelConnectionId}>
          Check Later
        </StyledSingleActionButton>
      </StyledConnectGitHub>
    );
  }

  const needsReconnect =
    installFailed ||
    pollTimedOut ||
    installationStatus === GitHubInstallationStatus.Suspended ||
    installationStatus === GitHubInstallationStatus.Deleted;

  const prompt = pollTimedOut
    ? 'This is taking longer than expected. Re-install to try again.'
    : needsReconnect
      ? 'The GitHub App connection isn’t active. Re-install to reconnect.'
      : 'Enter your GitHub organization, then install the Neo.Tax GitHub App.';

  // A non-null status means a previous attempt already armed the row: on a true first connect no
  // GITHUB_APP row exists until Install is clicked. So a plain PENDING on entry is a re-entry after
  // an install that never completed here, which needs the hint below just as much as the immediate
  // failure does — more so, since the user has by then lost the failure context. Kept separate from
  // `needsReconnect` so a bare re-entry doesn't get the "Action needed" framing.
  const showAlreadyInstalledHint = needsReconnect || installationStatus === GitHubInstallationStatus.Pending;

  // Retrying assumes the App is not installed on the org yet. When the install itself succeeded and
  // only our leg of the handshake was lost, GitHub sends the user to the App's configuration page,
  // which never fires the install callback — a dead end with no way back here. GitHub gives us no
  // way to tell the two apart from this side, so the retry copy names the recovery instead.
  const alreadyInstalledHint =
    'If GitHub shows a configuration page instead of the install screen, the App is already installed on your ' +
    'organization. Uninstall it from your organization’s Installed GitHub Apps settings, then install it again here.';

  return (
    <StyledConnectGitHub>
      <ModalHeader
        icon={<StyledAutomationsIcon />}
        iconBgColor={colors.blue[50]}
        subtitle={needsReconnect ? 'Action needed' : 'Step 2 of 2'}
        title={needsReconnect ? 'Reconnect GitHub' : 'Install the Neo.Tax GitHub App'}
      />
      <StyledGitHubBody direction="column">
        {providerLogo}
        <StyledGitHubPrompt as="p" fontSize="sm">
          {prompt}
        </StyledGitHubPrompt>
        {showAlreadyInstalledHint && (
          <StyledGitHubPrompt as="p" fontSize="sm">
            {alreadyInstalledHint}
          </StyledGitHubPrompt>
        )}
        {error && (
          <Alert
            severity="error"
            title="Couldn’t start the GitHub install"
            subtitle={<p>Something went wrong reaching GitHub. Please try again.</p>}
          />
        )}
        <Input
          id={ORGANIZATION_INPUT_ID}
          name={ORGANIZATION_INPUT_ID}
          // The placeholder is the only visible prompt, and a placeholder stops being an accessible
          // name as soon as the field has a value.
          aria-label="GitHub organization"
          value={organizationSlug}
          onChange={(event) => setOrganizationSlug(event.target.value)}
          placeholder="GitHub organization (e.g. acme-inc)"
        />
      </StyledGitHubBody>
      <MultipleActions
        actions={[
          {
            button: {
              variant: 'white',
              rounded: 'regular',
              size: 'base',
              onClick: restart,
              children: 'Cancel and Go Back',
              id: cancelConnectionId,
            },
            actionText: 'If you no longer wish to continue',
          },
          {
            button: {
              variant: 'primary',
              rounded: 'regular',
              size: 'base',
              disabled: loading || organizationSlug.trim() === '',
              onClick: () => install(organizationSlug.trim()),
              children: needsReconnect ? 'Re-connect GitHub' : 'Install on GitHub',
              id: connectId,
            },
            actionText: 'You must be a GitHub organization admin',
          },
        ]}
      />
    </StyledConnectGitHub>
  );
};
